function stripVietnameseMarks(value: string) {
  return value
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .replace(/đ/g, 'd')
    .replace(/Đ/g, 'D');
}

function cleanSpecText(input: string) {
  return String(input || '')
    .replace(/\s+/g, ' ')
    .replace(
      /^(thông tin chung|thong tin chung|thông số kỹ thuật|thong so ky thuat|thông số|thong so|specs?|cấu hình|cau hinh)\s*[-:]\s*/i,
      ''
    )
    .replace(/^[\p{L}\s]{2,28}\s*[-:]\s*/u, '')
    .trim();
}

function cleanProductNameText(input: string) {
  return String(input || '')
    .replace(/\s+/g, ' ')
    .replace(
      /^(thông tin chung|thong tin chung|thông số kỹ thuật|thong so ky thuat|thông số|thong so|specs?|cấu hình|cau hinh)\s*[-:]\s*/i,
      ''
    )
    .trim();
}

function compactSpec(input: string) {
  const cleaned = input
    .replace(/\*/g, '')
    .replace(/\b80\s*PLUS\b/gi, '80 Plus')
    .replace(/\bNon\s*Modular\b/gi, 'Non-Modular')
    .replace(/\s+/g, ' ')
    .trim();
  if (cleaned.length <= 32) return cleaned;
  return `${cleaned.slice(0, 32).trim()}...`;
}

function isGenericCategoryText(input: string) {
  const normalized = stripVietnameseMarks(input).toLowerCase().trim();
  return /^(laptop|chuot(?: may tinh| gaming)?|ban phim|tai nghe|man hinh|camera|webcam|pc|may in|linh kien|san pham)$/i.test(
    normalized
  );
}

function uniquePush(list: string[], value?: string | null) {
  const cleaned = compactSpec(String(value || '').trim());
  if (!cleaned || isGenericCategoryText(cleaned)) return;
  const key = canonicalSpecText(cleaned);
  if (!key) return;
  if (list.some((item) => canonicalSpecText(item) === key)) return;
  if (
    list.some(
      (item) => canonicalSpecText(item).includes(key) || key.includes(canonicalSpecText(item))
    )
  ) {
    return;
  }
  list.push(cleaned);
}

function canonicalSpecText(input: string) {
  return stripVietnameseMarks(input)
    .toLowerCase()
    .replace(/^[^:]{2,28}:\s*/g, '')
    .replace(/\bgb\b/g, 'g')
    .replace(/\btb\b/g, 't')
    .replace(/\s+/g, '')
    .replace(/[^a-z0-9"+.]/g, '');
}

function normalizeFormFactor(value: string) {
  if (/^(?:m|micro)[-\s]?atx$/i.test(value.trim())) return 'Micro-ATX';
  return value.replace(/\bmini[-\s]?itx\b/i, 'Mini-ITX').replace(/\be[-\s]?atx\b/i, 'E-ATX');
}

function normalizeRamSlot(value: string) {
  return value
    .replace(/\s+/g, ' ')
    .replace(/\b(ddr[345])\b/i, (match) => match.toUpperCase())
    .trim();
}

function pickMainboardSpecs(buckets: SpecBucket) {
  const result: string[] = [];
  const ramGenerations = buckets.ram.filter((item) => /^DDR[345]$/i.test(item));
  const ramSlots = buckets.ramSlots.map((item) => {
    const normalized = normalizeRamSlot(item);
    if (/\bDDR[345]\b/i.test(normalized)) return normalized;
    const generation = ramGenerations[0];
    return generation
      ? `${normalized.replace(/\s*RAM$/i, '')} ${generation.toUpperCase()}`
      : normalized;
  });

  [
    ...buckets.chipset,
    ...buckets.socket,
    ...buckets.formFactor,
    ...ramSlots,
    ...ramGenerations,
    ...buckets.connection.filter((item) => /^Wi-?Fi/i.test(item)),
  ].forEach((item) => uniquePush(result, item));

  return result.slice(0, 4);
}

type SpecBucket = {
  chipset: string[];
  socket: string[];
  formFactor: string[];
  ramSlots: string[];
  cpu: string[];
  gpu: string[];
  ram: string[];
  storage: string[];
  display: string[];
  refresh: string[];
  power: string[];
  psuStandard: string[];
  cable: string[];
  warranty: string[];
  connection: string[];
  weight: string[];
  other: string[];
};

type StructuredSpecEntry = {
  key: string;
  normalizedKey: string;
  value: string;
};

type ProductKind =
  | 'laptop'
  | 'monitor'
  | 'mouse'
  | 'keyboard'
  | 'cpu'
  | 'vga'
  | 'ram'
  | 'storage'
  | 'psu'
  | 'ups'
  | 'webcam'
  | 'printer'
  | 'mainboard'
  | 'generic';

function createBuckets(): SpecBucket {
  return {
    chipset: [],
    socket: [],
    formFactor: [],
    ramSlots: [],
    cpu: [],
    gpu: [],
    ram: [],
    storage: [],
    display: [],
    refresh: [],
    power: [],
    psuStandard: [],
    cable: [],
    warranty: [],
    connection: [],
    weight: [],
    other: [],
  };
}

function normalizeSpecLookupText(value: string) {
  return stripVietnameseMarks(value)
    .toLowerCase()
    .replace(/[_–—]/g, '-')
    .replace(/\s+/g, ' ')
    .trim();
}

function cleanStructuredSpecValue(input: string) {
  return String(input || '')
    .replace(/\*/g, '')
    .replace(/\s+/g, ' ')
    .replace(/\b(\d+(?:\.\d+)?)\s*-\s*(inch)\b/gi, '$1-$2')
    .replace(/\b(\d+(?:\.\d+)?)\s*(inch)\b/gi, '$1 inch')
    .replace(/\b(\d+(?:\.\d+)?)\s*(tb|gb|mb|rpm|w|va)\b/gi, (_, amount, unit) => {
      const normalizedUnit = String(unit).toUpperCase();
      return `${amount}${normalizedUnit === 'RPM' ? ' RPM' : normalizedUnit}`;
    })
    .replace(/\bgen\s*(\d)/gi, 'Gen $1')
    .replace(/\bsata\s*6\s*gb\/s\b/gi, 'SATA 6Gb/s')
    .replace(/\busb\s*3\.2\s*gen\s*1\b/gi, 'USB 3.2 Gen 1')
    .replace(/\busb\s*3\.1\b/gi, 'USB 3.1')
    .trim();
}

function getStructuredSpecEntries(fullSpecs?: Record<string, string>): StructuredSpecEntry[] {
  return Object.entries(fullSpecs || {})
    .map(([key, value]) => ({
      key,
      normalizedKey: normalizeSpecLookupText(key),
      value: cleanStructuredSpecValue(String(value || '')),
    }))
    .filter((entry) => entry.value);
}

function findStructuredValue(
  entries: StructuredSpecEntry[],
  patterns: RegExp[],
  valueGuard?: (value: string) => boolean
) {
  const entry = entries.find(
    (item) =>
      patterns.some((pattern) => pattern.test(item.normalizedKey)) &&
      (!valueGuard || valueGuard(item.value))
  );
  return entry?.value;
}

function addStructuredValue(list: string[], value?: string | null) {
  if (!value) return;
  uniquePush(list, value);
}

function buildStructuredSpecBadges(kind: ProductKind, fullSpecs?: Record<string, string>) {
  const entries = getStructuredSpecEntries(fullSpecs);
  if (!entries.length) return [];

  const result: string[] = [];
  const add = (patterns: RegExp[], guard?: (value: string) => boolean) => {
    addStructuredValue(result, findStructuredValue(entries, patterns, guard));
  };

  if (kind === 'storage') {
    add([/dung luong|capacity/]);
    add([/chuan giao tiep|ket noi.*chuan|interface/]);
    add([/rpm|toc do quay/]);
    return result.slice(0, 3);
  }

  if (kind === 'laptop') {
    add([/loai cpu|model cpu|cpu$/]);
    add([/card man hinh.*model|vga|gpu/]);
    add([/bo nho ram|ram - bo nho|dung luong ram/]);
    add([/luu tru.*dung luong|dung luong.*ssd|o cung/]);
    add([/man hinh.*kich thuoc|kich thuoc.*man hinh/]);
    return result.slice(0, 5);
  }

  if (kind === 'monitor') {
    add([/kich thuoc|size/], (value) => /\b\d{2}(?:\.\d+)?\s*(?:inch|")\b/i.test(value));
    add([/do phan giai|resolution/]);
    add([/tan so quet|refresh|hz/]);
    add([/tam nen|panel/]);
    add([/cong ket noi|chuan giao tiep|interface/]);
    add([/bao hanh|warranty/]);
    return result.slice(0, 6);
  }

  if (kind === 'cpu') {
    add([/loai cpu|model cpu|ten cpu|cpu$/]);
    add([/socket|loai socket/]);
    add([/so nhan|cores?/]);
    add([/so luong|threads?|so luong/]);
    add([/toc do toi da|boost|max|turbo/]);
    add([/tdp|cong suat|power/]);
    add([/bao hanh|warranty/]);
    return result.slice(0, 6);
  }

  if (kind === 'vga') {
    add([/dung luong|vram|bo nho/]);
    add([/so nhan cuda|cuda/]);
    add([/cong suat|tdp|power|psu/]);
    return result.slice(0, 3);
  }

  if (kind === 'ram') {
    add([/dung luong|capacity|bo nho ram|ram - bo nho/]);
    add([/loai ram|chuan ram|memory type|ddr/]);
    add([/toc do|bus|speed|mhz/]);
    add([/do tre|latency|cas|cl\b/]);
    add([/form factor|kieu ram|dimm|sodimm/]);
    add([/bao hanh|warranty/]);
    return result.slice(0, 6);
  }

  if (kind === 'mainboard') {
    add([/chipset/]);
    add([/socket/]);
    add([/form factor|kich thuoc|mainboard size/]);
    add([/khe ram|so khe ram|ram slot/]);
    add([/loai ram|chuan ram|memory type|ddr/]);
    add([/wifi|wireless/]);
    add([/bao hanh|warranty/]);
    return result.slice(0, 6);
  }

  if (kind === 'mouse') {
    add([/cam bien|sensor/]);
    add([/dpi/]);
    add([/ket noi|connect|wireless|receiver/]);
    add([/trong luong|weight/]);
    add([/bao hanh|warranty/]);
    return result.slice(0, 6);
  }

  if (kind === 'keyboard') {
    add([/switch/]);
    add([/layout|so phim/]);
    add([/ket noi|connect|wireless|receiver/]);
    add([/led|rgb|backlight/]);
    add([/bao hanh|warranty/]);
    return result.slice(0, 6);
  }

  if (kind === 'ups') {
    add([/cong suat|capacity|va|watt|power/]);
    add([/dien ap dau ra|output voltage/]);
    add([/thoi gian chuyen mach|transfer time/]);
    add([/dang song|wave/]);
    add([/form|kieu dang/]);
    add([/bao hanh|warranty/]);
    return result.slice(0, 6);
  }

  if (kind === 'webcam') {
    add([/do phan giai|resolution/]);
    add([/toc do khung hinh|fps|frame/]);
    add([/goc nhin|fov/]);
    add([/lay net|focus/]);
    add([/micro|mic/]);
    add([/giao tiep|ket noi|connect|usb|interface/]);
    add([/bao hanh|warranty/]);
    return result.slice(0, 6);
  }

  if (kind === 'printer') {
    add([/chuc nang|function/]);
    add([/cong nghe in|print technology/]);
    add([/kho giay|kho in|a4|a3|paper size/]);
    add([/toc do in|print speed/]);
    add([/ket noi|giao tiep|wifi|usb|interface/]);
    add([/muc su dung|hop muc|ink/]);
    add([/bao hanh|warranty/]);
    return result.slice(0, 6);
  }

  if (kind === 'psu') {
    add([/cong suat|watt|power/]);
    add([/hieu suat|80 plus|certification/]);
    add([/kieu day|modular/]);
    add([/form factor|kich thuoc/]);
    add([/bao hanh|warranty/]);
    return result.slice(0, 6);
  }

  if (kind === 'generic') {
    add([/dung luong|capacity/]);
    add([/do phan giai|resolution/]);
    add([/toc do|speed|fps|frame|hz/]);
    add([/ket noi|giao tiep|connect|usb|interface/]);
    add([/kich thuoc|size|form factor/]);
    add([/bao hanh|warranty/]);
    return result.slice(0, 6);
  }

  return [];
}

function pushMatches(
  list: string[],
  text: string,
  pattern: RegExp,
  transform?: (value: string) => string
) {
  for (const match of text.matchAll(pattern)) {
    uniquePush(list, transform ? transform(match[0]) : match[0]);
  }
}

function collectSpecKeywords(input: string, buckets: SpecBucket, key = '') {
  const keyText = stripVietnameseMarks(key).toLowerCase();
  const text = keyText.includes('ten san pham')
    ? cleanProductNameText(input)
    : cleanSpecText(input);
  if (!text) return;

  const normalized = stripVietnameseMarks(`${key} ${text}`).toLowerCase();
  const isRam = /\b(ram|memory|bo nho)\b/.test(normalized);
  const isStorage = /\b(ssd|hdd|o cung|storage|nvme|sata|m\.2)\b/.test(normalized);
  const isDisplay = /\b(man hinh|display|screen|inch|fhd|qhd|uhd|oled|ips|va|2k|4k)\b/.test(
    normalized
  );
  const isPower = /\b(tdp|cong suat|power|nguon|watt|pin|battery)\b/.test(normalized);
  const isWarranty = /\b(bao hanh|warranty|thang|month)\b/.test(normalized);
  const isConnection = /\b(wifi|bluetooth|wireless|2\.4g|usb|type-c|receiver|ket noi)\b/.test(
    normalized
  );
  const isWeight = /\b(can nang|weight|kg|gram|g\b)\b/.test(normalized);

  pushMatches(
    buckets.chipset,
    text,
    /\b(?:(?:Intel|AMD)\s+)?(?:H|B|Z|X|A)\d{3}[A-Z]?(?:-[A-Z0-9]+)?\b/gi
  );
  pushMatches(buckets.socket, text, /\b(?:LGA\s?\d{4}|AM[45])\b/gi, (value) =>
    value.replace(/\s+/g, ' ')
  );
  pushMatches(
    buckets.formFactor,
    text,
    /\b(?:mATX|M-ATX|Micro-ATX|ATX|Mini-ITX|ITX|E-ATX)\b/gi,
    normalizeFormFactor
  );
  pushMatches(
    buckets.ramSlots,
    text,
    /\b\d+\s*khe\s*(?:RAM\s*)?(?:DDR[345])?\b/gi,
    normalizeRamSlot
  );

  const cpuPatterns = [
    /\b(?:Intel\s+)?(?:Core\s+)?i[3579][-\s]?\d{4,5}[A-Z]{0,3}\b/gi,
    /\b(?:Core\s+)?Ultra\s+[3579]\s+\d{3}[A-Z]{0,3}\b/gi,
    /\bRyzen\s+[3579]\s+\d{4}[A-Z0-9]{0,5}\b/gi,
  ];
  cpuPatterns.forEach((pattern) => {
    pushMatches(buckets.cpu, text, pattern, (value) =>
      value.replace(/\bIntel\s+Core\s+/i, '').replace(/\bCore\s+/i, '')
    );
  });

  const gpuPatterns = [
    /\bRTX\s?\d{4}(?:\s?Ti| Super)?\b/gi,
    /\bGTX\s?\d{4}(?:\s?Ti)?\b/gi,
    /\bRX\s?\d{4}(?:\s?XT)?\b/gi,
    /\bArc\s+[A-Z]\d{3}\b/gi,
    /\bIris\s+Xe\b/gi,
    /\bUHD(?:\s+Graphics)?\b/gi,
  ];
  gpuPatterns.forEach((pattern) => pushMatches(buckets.gpu, text, pattern));

  for (const match of text.matchAll(/\b\d+(?:\.\d+)?\s*(?:GB|TB)\b/gi)) {
    const value = match[0].replace(/\s+/g, ' ');
    if (isRam && !isStorage) uniquePush(buckets.ram, value);
    else if (isStorage || /\b(TB|SSD|HDD|NVMe|SATA|M\.2)\b/i.test(text))
      uniquePush(buckets.storage, value);
    else uniquePush(buckets.ram, value);
  }

  for (const match of text.matchAll(/\bDDR[345]\b/gi))
    uniquePush(buckets.ram, match[0].toUpperCase());
  for (const match of text.matchAll(/\b(?:NVMe|SATA|PCIe\s*(?:Gen\s*)?\d(?:\.\d)?|M\.2)\b/gi)) {
    uniquePush(buckets.storage, match[0]);
  }

  for (const match of text.matchAll(
    /\b\d+(?:\.\d+)?\s*(?:W|Wh)\b(?:\s*[-~]\s*\d+(?:\.\d+)?\s*(?:W|Wh)\b)?/gi
  )) {
    uniquePush(buckets.power, match[0].replace(/\s*[-~]\s*/g, ' - '));
  }

  pushMatches(
    buckets.psuStandard,
    text,
    /\b80\s*Plus\s*(?:White|Bronze|Silver|Gold|Platinum|Titanium)?\b/gi,
    (value) => value.replace(/\b80\s*Plus\b/i, '80 Plus')
  );
  if (keyText.includes('hieu suat')) {
    pushMatches(buckets.psuStandard, text, /\b\d{2,3}%\b/g);
  }
  pushMatches(buckets.cable, text, /\b(?:Full|Semi|Non)[-\s]?Modular\b/gi, (value) =>
    value.replace(/\bNon\s*Modular\b/i, 'Non-Modular')
  );
  if (keyText.includes('kieu day') && text.length <= 24) uniquePush(buckets.cable, text);
  pushMatches(buckets.connection, text, /\b\d+\s*x\s*(?:\(\d\+\d\)\s*)?(?:PCIe|SATA|Molex)\b/gi);

  for (const match of normalized.matchAll(/\b\d{1,3}\s*(?:thang|months?)\b/gi)) {
    uniquePush(buckets.warranty, match[0].replace(/thang/i, 'tháng'));
  }

  for (const match of text.matchAll(/\b\d{2,3}\s*Hz\b/gi)) uniquePush(buckets.refresh, match[0]);
  for (const match of text.matchAll(/\b\d{2}(?:\.\d)?\s*(?:inch|")\b/gi)) {
    uniquePush(buckets.display, match[0].replace(/inch/i, '"'));
  }
  for (const match of text.matchAll(/\b(?:FHD|QHD|UHD|2K|4K|OLED|IPS|VA)\b/gi)) {
    uniquePush(buckets.display, match[0].toUpperCase());
  }

  pushMatches(
    buckets.connection,
    text,
    /\b(?:Wi-?Fi\s?\d?|Bluetooth\s?\d?(?:\.\d)?|2\.4G(?:Hz)?|USB(?:-C| Type-C)?|Type-C)\b/gi
  );
  pushMatches(buckets.weight, text, /\b\d+(?:\.\d+)?\s*(?:kg|g|gram)\b/gi);

  if (isWarranty && !buckets.warranty.length) {
    pushMatches(buckets.warranty, text, /\b\d{1,3}\b/g, (value) => `${value} tháng`);
  }

  if (
    !buckets.chipset.length &&
    !buckets.socket.length &&
    !buckets.formFactor.length &&
    !buckets.ramSlots.length &&
    !buckets.cpu.length &&
    !buckets.gpu.length &&
    !buckets.ram.length &&
    !buckets.storage.length &&
    !buckets.display.length &&
    !buckets.refresh.length &&
    !buckets.power.length &&
    !buckets.psuStandard.length &&
    !buckets.cable.length &&
    !buckets.warranty.length &&
    !buckets.connection.length &&
    !buckets.weight.length &&
    (keyText || text.length <= 24)
  ) {
    uniquePush(buckets.other, text);
  }
}

function inferProductKind(
  productName: string,
  specs?: string | null,
  fullSpecs?: Record<string, string>
): ProductKind {
  const text = stripVietnameseMarks(
    `${productName} ${specs || ''} ${Object.keys(fullSpecs || {}).join(' ')} ${Object.values(fullSpecs || {}).join(' ')}`
  ).toLowerCase();

  if (
    /\blaptop|notebook|macbook|thinkpad|ideapad|vivobook|expertbook|predator|aspire|nitro|tuf|rog\b/.test(
      text
    )
  )
    return 'laptop';
  if (/\bwebcam|camera|fov|fps\b/.test(text)) return 'webcam';
  if (/\bmay in|printer|epson|canon|brother|hp laserjet|ink\b/.test(text)) return 'printer';
  if (/\bups|bo luu dien|online|offline|\d{3,5}\s*va\b/.test(text)) return 'ups';
  if (/\bman hinh|monitor|\d{2}(?:\.\d)?\s*(?:inch|")\b/.test(text)) return 'monitor';
  if (/\bchuot|mouse|lightspeed|hero|dpi\b/.test(text)) return 'mouse';
  if (/\bban phim|keyboard|switch|keycap\b/.test(text)) return 'keyboard';
  if (/\bmainboard|motherboard|bo mach|(?:h|b|z|x|a)\d{3}[a-z]?(?:-[a-z0-9]+)?\b/.test(text))
    return 'mainboard';
  if (/\bcpu|processor|core i[3579]|ryzen [3579]\b/.test(text)) return 'cpu';
  if (/\bvga|card man hinh|geforce|radeon|rtx|gtx|rx \d{4}\b/.test(text)) return 'vga';
  if (/\bram|memory|ddr[345]\b/.test(text)) return 'ram';
  if (/\bssd|hdd|nvme|sata|o cung\b/.test(text)) return 'storage';
  if (/\bpsu|nguon|power supply|\d{3,4}\s*w\b/.test(text)) return 'psu';
  return 'generic';
}

function pickByKind(kind: ProductKind, buckets: SpecBucket) {
  const result: string[] = [];
  const take = (...groups: string[][]) => {
    groups.forEach((group) => group.forEach((item) => uniquePush(result, item)));
  };

  if (kind === 'laptop')
    take(
      buckets.cpu,
      buckets.gpu,
      buckets.ram,
      buckets.storage,
      buckets.display,
      buckets.refresh,
      buckets.power,
      buckets.weight,
      buckets.warranty
    );
  else if (kind === 'monitor')
    take(
      buckets.display,
      buckets.refresh,
      buckets.connection,
      buckets.power,
      buckets.warranty,
      buckets.other
    );
  else if (kind === 'mouse')
    take(buckets.connection, buckets.power, buckets.weight, buckets.warranty, buckets.other);
  else if (kind === 'keyboard') take(buckets.connection, buckets.other, buckets.warranty);
  else if (kind === 'cpu') take(buckets.cpu, buckets.power, buckets.warranty, buckets.other);
  else if (kind === 'vga')
    take(buckets.gpu, buckets.ram, buckets.power, buckets.warranty, buckets.other);
  else if (kind === 'ram') take(buckets.ram, buckets.warranty, buckets.other);
  else if (kind === 'storage') take(buckets.storage, buckets.warranty, buckets.other);
  else if (kind === 'ups') take(buckets.power, buckets.warranty, buckets.other);
  else if (kind === 'webcam')
    take(
      buckets.display,
      buckets.refresh,
      buckets.connection,
      buckets.weight,
      buckets.warranty,
      buckets.other
    );
  else if (kind === 'printer')
    take(buckets.connection, buckets.power, buckets.warranty, buckets.other);
  else if (kind === 'psu')
    take(
      buckets.power,
      buckets.psuStandard,
      buckets.formFactor,
      buckets.cable,
      buckets.warranty,
      buckets.connection,
      buckets.other
    );
  else if (kind === 'mainboard') return pickMainboardSpecs(buckets);
  else
    take(
      buckets.cpu,
      buckets.gpu,
      buckets.ram,
      buckets.storage,
      buckets.display,
      buckets.refresh,
      buckets.power,
      buckets.warranty,
      buckets.connection,
      buckets.weight,
      buckets.other
    );

  return result.slice(0, 6);
}

export function extractProductSpecKeywords(input: string) {
  const buckets = createBuckets();
  collectSpecKeywords(input, buckets);
  return [
    ...buckets.cpu,
    ...buckets.gpu,
    ...buckets.ram,
    ...buckets.storage,
    ...buckets.display,
    ...buckets.refresh,
    ...buckets.power,
    ...buckets.psuStandard,
    ...buckets.cable,
    ...buckets.warranty,
    ...buckets.connection,
    ...buckets.weight,
    ...buckets.other,
  ].slice(0, 6);
}

function getCardSpecLimit(kind: ProductKind) {
  return kind === 'laptop' ? 5 : 3;
}

function cleanTitleSpecItem(input: string) {
  return cleanStructuredSpecValue(input)
    .replace(/^[-–—|:/\s]+/, '')
    .replace(/\b(\d+)\s*CUDA\b/gi, '$1 CUDA')
    .replace(/\bSata\s+III\b/gi, 'Sata III')
    .replace(/\b(\d+)\s*GB\s+GDDR/gi, '$1GB GDDR')
    .replace(/\b(\d+)\s*GB\s+DDR/gi, '$1GB DDR')
    .replace(/\bSSD\s+(\d+)\s*GB\b/gi, 'SSD $1GB')
    .replace(/\s+/g, ' ')
    .trim();
}

function isUsefulTitleSpec(kind: ProductKind, item: string) {
  const normalized = stripVietnameseMarks(item).toLowerCase().trim();
  if (!normalized) return false;
  if (/^(mau|den|trang|xam|bac|blue|black|white|gray|silver)\b/.test(normalized)) return false;
  if (/\b(win(?:dows)?\s*11|balo|cap|hdsd|moi|chinh hang)\b/.test(normalized)) return false;
  if (kind === 'storage' && /\b(portable external|mau|2\.5|3\.5)\b/.test(normalized)) return false;
  return /(\d|ddr|gddr|ssd|hdd|nvme|sata|usb|wifi|rtx|gtx|rx|core|ryzen|intel|amd|ips|va|oled|fhd|qhd|uhd|wqxga|online|offline|switch|dpi|hz|inch|")/.test(
    normalized
  );
}

function getTitleSummarySpecs(productName: string, kind: ProductKind) {
  const titleParts = String(productName || '')
    .split('|')
    .map((part) => part.trim())
    .filter(Boolean);
  if (titleParts.length < 2) return [];

  const summaryText = titleParts.slice(1).join('|');
  const rawItems = summaryText
    .split(/[|,;]+/)
    .map(cleanTitleSpecItem)
    .filter((item) => isUsefulTitleSpec(kind, item));

  const result: string[] = [];
  rawItems.forEach((item) => uniquePush(result, item));

  if (kind === 'laptop') {
    return result
      .slice()
      .sort((a, b) => getLaptopSpecRank(a) - getLaptopSpecRank(b))
      .slice(0, getCardSpecLimit(kind));
  }

  if (kind === 'ups') {
    const capacity = result.filter((item) => /\b\d{3,5}\s*(?:VA|W)\b/i.test(item));
    const other = result.filter((item) => !capacity.includes(item));
    return [...capacity, ...other].slice(0, getCardSpecLimit(kind));
  }

  return result.slice(0, getCardSpecLimit(kind));
}

function getLaptopSpecRank(item: string) {
  const normalized = stripVietnameseMarks(item).toLowerCase();
  if (/\b(?:core\s*)?i[3579]|ryzen|r[3579]\b|u[3579]\b/.test(normalized)) return 0;
  if (/\b(?:rtx|gtx|rx|arc|radeon|geforce|iris|uhd)\b/.test(normalized)) return 1;
  if (/\b(?:ram|ddr[345]|lpddr|^\d+\s*gb$)/.test(normalized)) return 2;
  if (/\b(?:ssd|hdd|nvme|sata|m\.2)\b/.test(normalized)) return 3;
  if (/\b(?:inch|"|fhd|qhd|wqxga|uhd|ips|oled|hz)\b/.test(normalized)) return 4;
  return 9;
}

function getTinhocngoisaoStyleSpecs(
  kind: ProductKind,
  fullSpecs: Record<string, string> | undefined,
  productName: string
) {
  const titleSpecs = getTitleSummarySpecs(productName, kind);

  if (kind === 'storage') {
    const structured = buildStructuredSpecBadges(kind, fullSpecs);
    if (structured.length) return structured;
    return titleSpecs;
  }

  if (['vga', 'laptop', 'monitor', 'ram', 'mainboard', 'ups', 'printer', 'psu'].includes(kind)) {
    if (titleSpecs.length >= 2) return titleSpecs;
  }

  const structured = buildStructuredSpecBadges(kind, fullSpecs);
  if (structured.length) return structured.slice(0, getCardSpecLimit(kind));

  return titleSpecs;
}

export function buildProductCardSpecBadges(
  specs?: string | null,
  fullSpecs?: Record<string, string>,
  productName = ''
) {
  const kind = inferProductKind(productName, specs, fullSpecs);
  const sourceStyleSpecs = getTinhocngoisaoStyleSpecs(kind, fullSpecs, productName);
  if (sourceStyleSpecs.length) {
    return sourceStyleSpecs;
  }

  const buckets = createBuckets();

  collectSpecKeywords(productName, buckets, 'Tên sản phẩm');

  Object.entries(fullSpecs || {}).forEach(([key, value]) => {
    collectSpecKeywords(String(value || ''), buckets, key);
  });

  const fromText = String(specs || '')
    .split(/[,|•\n]/)
    .map((item) => item.trim())
    .filter(Boolean);

  fromText.forEach((item) => collectSpecKeywords(item, buckets));

  return pickByKind(kind, buckets).slice(0, getCardSpecLimit(kind));
}
